import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useLocation, useHistory, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Axios from 'axios';
import { Auth } from 'aws-amplify';
import Head from 'src/layout/head/Head';
import { API_ROUTES } from 'src/config/api';
import { getStepTypeBadgeColor, getScheduleLabel, getPlaybookDescription, getPlaybookSchedule, getStepsDetail } from './data';
import {
  ArrowLeftIcon,
  PencilIcon,
  PlayIcon,
  PauseIcon,
  ClockIcon,
  CommandLineIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  StopIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon,
  ViewfinderCircleIcon,
  EyeIcon,
  CubeIcon,
  ArrowRightIcon,
  HashtagIcon,
  KeyIcon,
  PlusIcon,
  TrashIcon,
  CodeBracketIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  FunnelIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Handle,
  Position,
  useReactFlow,
  useNodesState,
  useEdgesState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';

// Integration and node type icons mapping
const IntegrationIcon = ({ integration, nodeType, className = "w-4 h-4" }) => {
  // Integration-specific icons
  const integrationIcons = {
    felloh: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
      </svg>
    ),
    pipedrive: (
      <div className={`${className} rounded bg-green-600 flex items-center justify-center text-white font-bold text-xs`}>P</div>
    ),
    xero: (
      <div className={`${className} rounded bg-cyan-500 flex items-center justify-center text-white font-bold text-xs`}>X</div>
    ),
    sage: (
      <div className={`${className} rounded bg-green-500 flex items-center justify-center text-white font-bold text-xs`}>S</div>
    ),
    email: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    slack: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 15a2 2 0 01-2 2 2 2 0 01-2-2 2 2 0 012-2h2v2zm1 0a2 2 0 012-2 2 2 0 012 2v5a2 2 0 01-2 2 2 2 0 01-2-2v-5zm2-8a2 2 0 01-2-2 2 2 0 012-2 2 2 0 012 2v2H9zm0 1a2 2 0 012 2 2 2 0 01-2 2H4a2 2 0 01-2-2 2 2 0 012-2h5zm8 2a2 2 0 012-2 2 2 0 012 2 2 2 0 01-2 2h-2v-2zm-1 0a2 2 0 01-2 2 2 2 0 01-2-2V5a2 2 0 012-2 2 2 0 012 2v5zm-2 8a2 2 0 012 2 2 2 0 01-2 2 2 2 0 01-2-2v-2h2zm0-1a2 2 0 01-2-2 2 2 0 012-2h5a2 2 0 012 2 2 2 0 01-2 2h-5z" />
      </svg>
    )
  };

  if (integration && integrationIcons[integration]) {
    return integrationIcons[integration];
  }

  // Node type icons (for nodes without specific integration)
  const type = nodeType || '';

  // Webhook/trigger icon
  if (type.includes('webhook') || type.includes('trigger')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  }

  // Code icon
  if (type.includes('code')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
      </svg>
    );
  }

  // If/Filter icon
  if (type.includes('if') || type.includes('filter')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
      </svg>
    );
  }

  // HTTP Request icon
  if (type.includes('httpRequest')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    );
  }

  // Set/transform icon
  if (type.includes('set')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="9" y1="3" x2="9" y2="21" />
      </svg>
    );
  }

  // Split icon
  if (type.includes('split')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
      </svg>
    );
  }

  // Default icon (cube/step)
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    </svg>
  );
};

// Simple connector line for legacy steps list
const Connector = ({ height = 'h-6' }) => (
  <div className={`w-0.5 ${height} bg-violet-500 dark:bg-violet-400`} />
);

// Get icon color based on node type or integration
const getNodeIconStyle = (node) => {
  const integration = node.integration;
  const type = node.type || '';

  if (integration === 'felloh') return { bg: 'bg-violet-500', text: 'text-white' };
  if (integration === 'pipedrive') return { bg: 'bg-emerald-500', text: 'text-white' };
  if (integration === 'xero') return { bg: 'bg-cyan-400', text: 'text-white' };
  if (integration === 'email') return { bg: 'bg-rose-400', text: 'text-white' };
  if (integration === 'slack') return { bg: 'bg-violet-500', text: 'text-white' };
  if (integration === 'sage') return { bg: 'bg-green-600', text: 'text-white' };

  if (type.includes('webhook') || type.includes('trigger')) return { bg: 'bg-teal-500', text: 'text-white' };
  if (type.includes('if') || type.includes('filter')) return { bg: 'bg-amber-500', text: 'text-white' };
  if (type.includes('switch')) return { bg: 'bg-rose-500', text: 'text-white' };
  if (type.includes('code')) return { bg: 'bg-orange-500', text: 'text-white' };
  if (type.includes('httpRequest')) return { bg: 'bg-blue-500', text: 'text-white' };
  if (type.includes('set')) return { bg: 'bg-indigo-400', text: 'text-white' };
  if (type.includes('split')) return { bg: 'bg-pink-500', text: 'text-white' };

  return { bg: 'bg-gray-500', text: 'text-white' };
};

// Get short subtitle for node
const getNodeSubtitle = (node) => {
  const type = node.type || '';
  const params = node.parameters || {};

  if (params.operation) return params.operation;
  if (params.path) return params.path;
  if (type.includes('webhook')) return 'webhook';
  if (type.includes('trigger')) return 'trigger';
  if (type.includes('filter')) return 'filter';
  if (type.includes('if')) return 'condition';
  if (type.includes('switch')) return 'router';
  if (type.includes('code')) return 'function';
  if (type.includes('httpRequest')) return 'HTTP request';
  if (type.includes('set')) return 'set values';
  if (type.includes('split')) return 'split data';

  return null;
};

// Custom React Flow Node for workflow steps - n8n style
const StepNode = ({ data, selected }) => {
  const { node, onClick } = data;
  const iconStyle = getNodeIconStyle(node);
  const subtitle = getNodeSubtitle(node);

  return (
    <div className="flex flex-col items-center">
      {/* Node card */}
      <div
        onClick={() => onClick && onClick(node)}
        className={`relative bg-gray-800 rounded-lg w-[56px] h-[56px] flex items-center justify-center shadow-lg transition-all hover:shadow-xl cursor-pointer ${selected
          ? 'ring-2 ring-violet-500 ring-offset-2 ring-offset-violet-50 dark:ring-offset-violet-950'
          : ''
          }`}
      >
        <Handle
          type="target"
          position={Position.Left}
          className="!bg-transparent !w-1 !h-1 !border-0 !-left-0.5"
        />

        {/* Icon container */}
        <div className={`w-8 h-8 rounded-md ${iconStyle.bg} flex items-center justify-center`}>
          <IntegrationIcon integration={node.integration} nodeType={node.type} className={`w-5 h-5 ${iconStyle.text}`} />
        </div>

        <Handle
          type="source"
          position={Position.Right}
          className="!bg-transparent !w-1 !h-1 !border-0 !-right-0.5"
        />
      </div>

      {/* Label below node */}
      <div className="mt-2 text-center">
        <div className="text-xs font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">
          {node.name}
        </div>
        {subtitle && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

// Custom React Flow Node for branch labels (Success, Error, etc.) - n8n style
const BranchLabelNode = ({ data }) => {
  const { label, type } = data;
  const styles = {
    success: 'text-emerald-400',
    error: 'text-rose-400',
    path: 'text-gray-400'
  };

  const handleClasses = {
    success: '!bg-emerald-500',
    error: '!bg-rose-500',
    path: '!bg-gray-500'
  };

  return (
    <div className="flex items-center">
      <Handle
        type="target"
        position={Position.Left}
        className={`!w-2.5 !h-2.5 !border-0 ${handleClasses[type] || handleClasses.path} !-left-1`}
      />
      <span className={`text-[10px] font-medium px-2 py-0.5 whitespace-nowrap ${styles[type] || styles.path}`}>
        {label}
      </span>
      <Handle
        type="source"
        position={Position.Right}
        className={`!w-2.5 !h-2.5 !border-0 ${handleClasses[type] || handleClasses.path} !-right-1`}
      />
    </div>
  );
};

// Node types for React Flow
const nodeTypes = {
  stepNode: StepNode,
  branchLabel: BranchLabelNode
};

// Determine integration from node type
const getIntegration = (type) => {
  if (!type) return null;
  if (type.includes('felloh')) return 'felloh';
  if (type.includes('pipedrive')) return 'pipedrive';
  if (type.includes('xero')) return 'xero';
  if (type.includes('slack')) return 'slack';
  if (type.includes('email') || type.includes('Email')) return 'email';
  if (type.includes('sage') || type.includes('Sage')) return 'sage';
  return null;
};

// Node dimensions for dagre layout
const NODE_WIDTH = 160;
const NODE_HEIGHT = 100;

// Create dagre graph and apply layout for horizontal flow
const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 120 });

  // Add nodes to dagre
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  // Add edges to dagre
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply positions to nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Convert n8n nodes and connections directly to React Flow format (supports convergence)
const convertN8nToFlow = (n8nNodes, connections, onNodeClick, selectedNodeId) => {
  if (!n8nNodes || n8nNodes.length === 0) return { nodes: [], edges: [] };

  const nodes = [];
  const edges = [];
  const nodeByName = {};

  // Build node lookup by name
  n8nNodes.forEach(node => {
    nodeByName[node.name] = node;
  });

  // Create React Flow nodes for each n8n node
  n8nNodes.forEach(node => {
    // Skip disabled nodes
    if (node.disabled) return;

    const flowNode = {
      id: node.id,
      type: 'stepNode',
      position: { x: 0, y: 0 },
      data: {
        node: {
          ...node,
          integration: getIntegration(node.type)
        },
        onClick: onNodeClick
      },
      selected: node.id === selectedNodeId
    };
    nodes.push(flowNode);
  });

  // Create edges from connections
  Object.entries(connections || {}).forEach(([sourceName, connectionData]) => {
    const sourceNode = nodeByName[sourceName];
    if (!sourceNode || sourceNode.disabled) return;

    if (connectionData?.main) {
      connectionData.main.forEach((outputs, outputIndex) => {
        if (!outputs || !Array.isArray(outputs)) return;

        outputs.forEach(conn => {
          const targetNode = nodeByName[conn.node];
          if (!targetNode || targetNode.disabled) return;

          // Determine edge color based on output index for branching nodes
          const isIfNode = sourceNode.type?.includes('if');
          let edgeColor = '#6b7280'; // default gray

          if (isIfNode) {
            // For if nodes: output 0 = true (green), output 1 = false (gray)
            edgeColor = outputIndex === 0 ? '#10b981' : '#6b7280';
          }

          // Create unique edge id
          const edgeId = `e-${sourceNode.id}-${outputIndex}-${targetNode.id}`;

          // Check if edge already exists (avoid duplicates)
          if (!edges.find(e => e.id === edgeId)) {
            edges.push({
              id: edgeId,
              source: sourceNode.id,
              target: targetNode.id,
              type: 'smoothstep',
              style: { stroke: edgeColor, strokeWidth: 2 },
              label: isIfNode ? (outputIndex === 0 ? 'true' : 'false') : undefined,
              labelStyle: { fill: '#6b7280', fontSize: 10, fontWeight: 500 },
              labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
              labelBgPadding: [4, 2],
              labelBgBorderRadius: 2
            });
          }
        });
      });
    }
  });

  // Apply dagre layout
  return getLayoutedElements(nodes, edges);
};

// Navigation controls for the tree view
const TreeViewControls = () => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  const handleZoomIn = () => {
    zoomIn();
  };

  const handleZoomOut = () => {
    zoomOut();
  };

  const handleFitView = () => {
    fitView({ padding: 0.2 });
  };

  const handleResetZoom = () => {
    fitView({ padding: 0.2, maxZoom: 1 });
  };

  return (
    <div className="absolute bottom-4 right-4 z-10 flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-violet-200 dark:border-gray-600 p-1 clearfix h-auto">
      <button
        onClick={handleZoomOut}
        className="p-2 hover:bg-violet-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-violet-700 dark:hover:text-white"
        title="Zoom out"
      >
        <MagnifyingGlassMinusIcon className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-violet-200 dark:bg-gray-600" />
      <button
        onClick={handleZoomIn}
        className="p-2 hover:bg-violet-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-violet-700 dark:hover:text-white"
        title="Zoom in"
      >
        <MagnifyingGlassPlusIcon className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-violet-200 dark:bg-gray-600" />
      <button
        onClick={handleFitView}
        className="p-2 hover:bg-violet-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-violet-700 dark:hover:text-white"
        title="Fit to view"
      >
        <ArrowsPointingOutIcon className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-violet-200 dark:bg-gray-600" />
      <button
        onClick={handleResetZoom}
        className="p-2 hover:bg-violet-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-violet-700 dark:hover:text-white"
        title="Reset zoom"
      >
        <ViewfinderCircleIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

// Inner component that uses React Flow hooks
const WorkflowTreeInner = ({ nodes: initialNodes, edges: initialEdges, onNodeClick }) => {
  const { fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Sync when parent data changes
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Fit view after nodes are measured
  const onNodesMeasured = useCallback(() => {
    setTimeout(() => fitView({ padding: 0.3 }), 50);
  }, [fitView]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={onNodeClick}
      onInit={() => {
        setTimeout(() => fitView({ padding: 0.3 }), 100);
        setTimeout(() => fitView({ padding: 0.3 }), 500);
      }}
      onNodeDimensionChange={onNodesMeasured}
      nodeTypes={nodeTypes}
      defaultEdgeOptions={{
        type: 'smoothstep',
        style: { strokeWidth: 2, stroke: '#6b7280' }
      }}
      fitView
      fitViewOptions={{ padding: 0.3, includeHiddenNodes: false }}
      minZoom={0.3}
      maxZoom={2}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      panOnDrag={true}
      zoomOnScroll={true}
      proOptions={{ hideAttribution: true }}
    >
      <Background color="#c4b5fd" gap={20} size={1} variant="dots" />
      <TreeViewControls />
    </ReactFlow>
  );
};

// Main workflow tree component using React Flow - n8n style with graph support
const WorkflowTree = ({ n8nNodes, connections, onNodeSelect, selectedNodeId }) => {
  const { nodes, edges } = useMemo(
    () => n8nNodes ? convertN8nToFlow(n8nNodes, connections, onNodeSelect, selectedNodeId) : { nodes: [], edges: [] },
    [n8nNodes, connections, onNodeSelect, selectedNodeId]
  );

  const onNodeClick = useCallback((_event, node) => {
    if (node.data.onClick && node.data.node) {
      node.data.onClick(node.data.node);
    }
  }, []);

  if (!n8nNodes || n8nNodes.length === 0) return null;

  return (
    <div className="h-[500px] w-full rounded-xl overflow-hidden border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950">
      <style>{`
        .react-flow__edges {
          width: 100% !important;
          height: 100% !important;
        }
        .react-flow__background {
          background-color: #f5f3ff !important;
        }
        .dark .react-flow__background {
          background-color: #2e1065 !important;
        }
        .react-flow__edge-text {
          font-size: 10px;
        }
      `}</style>
      <ReactFlowProvider>
        <WorkflowTreeInner nodes={nodes} edges={edges} onNodeClick={onNodeClick} />
      </ReactFlowProvider>
    </div>
  );
};

// Step detail panel shown when a tree node is clicked
const StepDetailPanel = ({ node, onClose, onNavigateToNode }) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [jsonValue, setJsonValue] = useState('');
  const [jsonError, setJsonError] = useState(null);

  if (!node) return null;

  const hasIntegration = node.integration;
  const configEntries = node.config
    ? Object.entries(node.config).filter(([key]) => key !== 'integration')
    : [];

  // Count children and branches
  const childrenCount = node.children?.length || 0;
  const branchesCount = node.branches?.length || 0;
  const totalConnections = childrenCount + (branchesCount > 0 ? node.branches.reduce((sum, b) => sum + (b.children?.length || 0), 0) : 0);

  // Get a friendly type name
  const getTypeName = (type) => {
    if (!type) return 'Step';
    if (type.includes('trigger')) return 'Trigger';
    if (type.includes('webhook')) return 'Webhook';
    if (type.includes('if')) return 'Condition';
    if (type.includes('switch')) return 'Switch';
    if (type.includes('code')) return 'Code';
    if (type.includes('http')) return 'HTTP Request';
    return type.split('.').pop() || 'Step';
  };

  // Prepare node data for JSON editor (exclude children/branches to avoid circular refs)
  const getEditableNodeData = () => {
    const { children, branches, ...editableData } = node;
    return editableData;
  };

  const handleOpenEditModal = () => {
    const editableData = getEditableNodeData();
    setJsonValue(JSON.stringify(editableData, null, 2));
    setJsonError(null);
    setEditModalOpen(true);
  };

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonValue(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  const handleSaveJson = () => {
    try {
      JSON.parse(jsonValue);
      // In a real implementation, this would update the node via an API call
      // For now, just close the modal
      setEditModalOpen(false);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-5 w-[480px] max-w-[540px] overflow-y-auto max-h-[calc(100vh-200px)]">
      {/* JSON Edit Modal */}
      <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)} size="lg">
        <ModalHeader
          toggle={() => setEditModalOpen(false)}
          className="bg-violet-800 text-xl text-white pt-4 pb-3"
          close={
            <button className="-mt-1 text-white" onClick={() => setEditModalOpen(false)}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          }
        >
          <span className="flex items-center gap-2">
            <CodeBracketIcon className="h-5 w-5" />
            Step Configuration: {node.name}
          </span>
        </ModalHeader>
        <ModalBody className="p-0">
          <pre className="w-full h-[500px] p-4 font-mono text-sm bg-gray-900 text-green-400 overflow-auto m-0">
            {jsonValue}
          </pre>
        </ModalBody>
        <ModalFooter className="bg-light">
          <button
            type="button"
            onClick={() => setEditModalOpen(false)}
            className="btn btn-outline-secondary"
          >
            Close
          </button>
        </ModalFooter>
      </Modal>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 clearfix h-auto">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {hasIntegration && (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${getStepTypeBadgeColor(null, node.integration)}`}>
                <IntegrationIcon integration={node.integration} className="w-4 h-4" />
                {node.integration.charAt(0).toUpperCase() + node.integration.slice(1)}
              </span>
            )}
            {node.type && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                <CubeIcon className="w-3.5 h-3.5" />
                {getTypeName(node.type)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleOpenEditModal}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            title="Edit step JSON"
          >
            <CodeBracketIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Title */}
      <h3 className={`text-lg font-semibold text-gray-900 dark:text-white mt-3 ${!node.description ? 'mb-4' : ''}`}>
        {node.name}
      </h3>

      {/* Description */}
      {node.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {node.description}
        </p>
      )}

      {/* Step Info Grid */}
      <div className="grid grid-cols-2 gap-3 clearfix h-auto">
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 pb-4 clearfix h-auto">
          <div className="flex items-center gap-1.5 mb-2">
            <HashtagIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Step ID</span>
          </div>
          <p className="text-sm font-mono text-gray-900 dark:text-white truncate" title={node.id}>
            {node.id}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 pb-4 clearfix h-auto">
          <div className="flex items-center gap-1.5 mb-2">
            <ArrowRightIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Connections</span>
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {totalConnections > 0 ? `${totalConnections} outgoing` : 'End of flow'}
          </p>
        </div>
      </div>

      {/* Node Type */}
      {node.type && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-4">
          <code className="text-xs text-violet-600 dark:text-violet-400 font-mono break-all">
            {node.type.replace('n8n-nodes-', '')}
          </code>
        </div>
      )}

      {/* Configuration */}
      {configEntries.length > 0 && (
        <div className="mb-4">
          <h4 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Configuration
          </h4>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 font-mono text-xs space-y-2 max-h-48 overflow-y-auto">
            {configEntries.map(([key, value]) => (
              <div key={key} className="break-all">
                <span className="text-gray-500 dark:text-gray-400">{key}:</span>{' '}
                <span className="text-green-600 dark:text-green-400">
                  {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Parameters / Code */}
      {node.parameters && Object.keys(node.parameters).length > 0 && (
        <div className="mb-4">
          <h4 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Parameters
          </h4>
          <pre className="p-3 font-mono text-xs bg-gray-900 text-green-400 overflow-auto max-h-[400px] rounded-lg m-0">
            {JSON.stringify(node.parameters, null, 2)}
          </pre>
        </div>
      )}

      {/* Branches */}
      {node.branches && node.branches.length > 0 && (
        <div className="mb-4">
          <h4 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Branches ({node.branches.length})
          </h4>
          <div className="space-y-2">
            {node.branches.map((branch) => (
              <div
                key={branch.id}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${branch.type === 'success'
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                  : branch.type === 'error'
                    ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
                  }`}
              >
                <span className="font-medium">{branch.label}</span>
                <span className="text-xs opacity-75">
                  {branch.children?.length || 0} step{(branch.children?.length || 0) !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Children */}
      {childrenCount > 0 && !branchesCount && (
        <div>
          <h4 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Next Steps ({childrenCount})
          </h4>
          <div className="space-y-1.5">
            {node.children.slice(0, 3).map((child) => (
              <button
                key={child.id}
                onClick={() => onNavigateToNode && onNavigateToNode(child)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-sm transition-colors text-left clearfix h-auto"
              >
                <ArrowRightIcon className="w-4 h-4 text-violet-500 dark:text-violet-400 flex-shrink-0" />
                <span className="text-gray-900 dark:text-white truncate flex-1">{child.name}</span>
                {child.integration && (
                  <span className="flex-shrink-0">
                    <IntegrationIcon integration={child.integration} className="w-4 h-4" />
                  </span>
                )}
              </button>
            ))}
            {childrenCount > 3 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-1">
                +{childrenCount - 3} more step{childrenCount - 3 !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Legacy flat steps renderer for playbooks without tree structure
const LegacyStepsList = ({ steps }) => {
  return (
    <div className="space-y-4">
      {steps.map((step, index) => (
        <div key={step.id} className="flex flex-col items-center">
          {index > 0 && <Connector height="h-6" />}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4 max-w-md w-full">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center">
                <span className="text-xs font-semibold text-violet-700 dark:text-violet-300">{index + 1}</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{step.name}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStepTypeBadgeColor(step.type)}`}>
                    {step.type}
                  </span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{step.description}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Step type icon for the steps view
const StepTypeIcon = ({ type }) => {
  const icons = {
    webhook: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    condition: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
      </svg>
    ),
    action: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
    delay: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    schedule: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    loop: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 1l4 4-4 4" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <path d="M7 23l-4-4 4-4" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    )
  };
  return icons[type] || icons.action;
};

// Format config value for display
const formatConfigValue = (value) => {
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return String(value);
};

// Steps view - flat list format
const StepItem = ({ node, index }) => {
  const [expanded, setExpanded] = useState(false);
  const integration = getIntegration(node.type);
  const nodeType = node.type || '';

  let stepType = 'action';
  if (nodeType.includes('webhook') || nodeType.includes('trigger')) stepType = 'trigger';
  else if (nodeType.includes('if') || nodeType.includes('filter')) stepType = 'condition';
  else if (nodeType.includes('switch')) stepType = 'router';
  else if (nodeType.includes('code')) stepType = 'code';
  else if (nodeType.includes('httpRequest')) stepType = 'http';

  const isConditional = nodeType.includes('if') || nodeType.includes('switch');

  return (
    <div className={`rounded-lg border ${isConditional ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30' : 'border-gray-200 dark:border-gray-700'}`}>
      <div className="p-4">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center -mt-1.5 ${isConditional ? 'bg-amber-100 dark:bg-amber-900/50' : 'bg-gray-100 dark:bg-gray-700'}`}>
            <span className={`text-sm font-medium ${isConditional ? 'text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-gray-300'}`}>{index + 1}</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap -mt-1">
              <span className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                {node.name}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${isConditional ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                <StepTypeIcon type={stepType} />
                {stepType}
              </span>
              {integration && (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStepTypeBadgeColor(null, integration)}`}>
                  <IntegrationIcon integration={integration} nodeType={nodeType} className="w-3.5 h-3.5" />
                  {integration}
                </span>
              )}
            </div>

            {node.notes && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {node.notes.substring(0, 100)}{node.notes.length > 100 ? '...' : ''}
              </p>
            )}
          </div>

          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-shrink-0 p-2 hover:bg-violet-100 dark:hover:bg-violet-900/50 rounded-md transition-colors text-violet-800 dark:text-violet-400 -mt-2"
            title={expanded ? 'Hide configuration' : 'View configuration'}
          >
            <CodeBracketIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <pre className="p-4 font-mono text-xs bg-gray-900 text-green-400 overflow-auto max-h-[300px] rounded-b-lg m-0">
            {JSON.stringify(node.parameters || {}, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

const StepsView = ({ n8nNodes }) => {
  if (!n8nNodes || n8nNodes.length === 0) return null;

  const activeNodes = n8nNodes.filter(node => !node.disabled);

  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
        Steps
      </div>
      <div className="space-y-3">
        {activeNodes.map((node, index) => (
          <StepItem key={node.id} node={node} index={index} />
        ))}
      </div>
    </div>
  );
};

// Secrets View component for managing playbook secrets
const SecretsView = ({ secrets, onAddSecret, onEditSecret, onDeleteSecret, isAdmin }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSecret, setEditingSecret] = useState(null);
  const [formData, setFormData] = useState({ key: '', value: '', description: '' });

  const handleOpenAddModal = () => {
    setFormData({ key: '', value: '', description: '' });
    setEditingSecret(null);
    setShowAddModal(true);
  };

  const handleOpenEditModal = (secret) => {
    setFormData({ key: secret.key, value: '', description: secret.description || '' });
    setEditingSecret(secret);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingSecret(null);
    setFormData({ key: '', value: '', description: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingSecret) {
      onEditSecret({ ...editingSecret, ...formData, updatedAt: new Date().toISOString() });
    } else {
      onAddSecret({
        id: `secret-${Date.now()}`,
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    handleCloseModal();
  };

  const maskValue = (value) => {
    if (!value) return '••••••••';
    return '•'.repeat(Math.min(value.length, 24));
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Secrets & Credentials</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          API keys, tokens, and other sensitive values required by this playbook. These are determined automatically when the playbook is generated.
        </p>
      </div>

      {secrets.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
          <KeyIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No secrets configured</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Add secrets to securely store API keys and credentials for your integrations.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {secrets.map((secret) => (
            <div
              key={secret.id}
              className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${secret.auth_type === 'oauth' ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-violet-100 dark:bg-violet-900/50'}`}>
                    <KeyIcon className={`w-5 h-5 ${secret.auth_type === 'oauth' ? 'text-blue-600 dark:text-blue-400' : 'text-violet-600 dark:text-violet-400'}`} />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white font-mono">
                      {secret.key}
                    </h4>
                    {secret.auth_type === 'oauth' && secret.provider && (
                      <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                        OAuth &middot; {secret.provider.charAt(0).toUpperCase() + secret.provider.slice(1)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {secret.auth_type === 'oauth' ? (
                    <a
                      href={`${process.env.REACT_APP_N8N_BASE_URL || 'https://playbooks.felloh.com'}/credentials`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
                    >
                      {secret.value_last_four ? 'Reconnect' : 'Connect'}
                    </a>
                  ) : (
                    <button
                      onClick={() => handleOpenEditModal(secret)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400"
                      title="Edit secret"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => onDeleteSecret(secret.id)}
                      className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                      title="Delete secret"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
              {secret.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-12 break-words">
                  {secret.description}
                </p>
              )}
              {secret.auth_type !== 'oauth' && (
                <div className="flex items-center gap-2 mt-4 ml-12">
                  <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-md px-3 py-2 font-mono text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      {maskValue(secret.value)}
                    </span>
                  </div>
                </div>
              )}
              {secret.auth_type === 'oauth' && (
                <div className="mt-4 ml-12">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${secret.value_last_four ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'}`}>
                    <span className={`w-2 h-2 rounded-full ${secret.value_last_four ? 'bg-green-500' : 'bg-amber-500'}`} />
                    {secret.value_last_four ? 'Connected' : 'Not connected'}
                  </div>
                </div>
              )}
              <div className="mt-3 ml-12 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                <span>Created {moment(secret.createdAt).format('MMM D, YYYY')}</span>
                {secret.updatedAt !== secret.createdAt && (
                  <span>Updated {moment(secret.updatedAt).fromNow()}</span>
                )}
                {secret.usedIn && (
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full">
                    Used in {secret.usedIn.length} step{secret.usedIn.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Secret Modal */}
      <Modal isOpen={showAddModal} toggle={handleCloseModal} size="md">
        <ModalHeader
          toggle={handleCloseModal}
          className="bg-violet-800 text-xl text-white pt-4 pb-3"
          close={
            <button className="-mt-1 text-white" onClick={handleCloseModal}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          }
        >
          <span className="flex items-center gap-2">
            <KeyIcon className="h-5 w-5" />
            {editingSecret ? 'Edit Secret' : 'Add Secret'}
          </span>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody className="p-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="secret-key" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Secret Name
                </label>
                <input
                  type="text"
                  id="secret-key"
                  value={formData.key}
                  onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') }))}
                  placeholder="e.g., XERO_API_KEY"
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 font-mono"
                  required
                  disabled={!!editingSecret}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Use SCREAMING_SNAKE_CASE. Only letters, numbers, and underscores allowed.
                </p>
              </div>

              <div>
                <label htmlFor="secret-value" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Secret Value
                </label>
                <input
                  type="password"
                  id="secret-value"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                  placeholder={editingSecret ? 'Enter new value to update' : 'Enter secret value'}
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 font-mono"
                  required={!editingSecret}
                />
                {editingSecret && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Leave empty to keep the existing value.
                  </p>
                )}
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 clearfix h-auto">
                <div className="flex items-start gap-3 clearfix h-auto">
                  <ExclamationCircleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200 clearfix h-auto">
                    <p className="font-medium">Security Notice</p>
                    <p className="mt-1 text-amber-700 dark:text-amber-300">
                      Secrets are encrypted at rest and only decrypted during playbook execution. Never share secret values in logs or error messages.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>

          <ModalFooter className="bg-light">
            <button
              type="button"
              onClick={handleCloseModal}
              className="btn btn-outline-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn bg-violet-700 hover:bg-violet-800 text-white"
            >
              {editingSecret ? 'Update Secret' : 'Add Secret'}
            </button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

// Format duration in human readable format
const formatDuration = (ms) => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Test Run Status Box component
const TestRunStatusBox = ({ testRun, onClose, onStop, stepsCount }) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef(null);

  const status = testRun?.status;
  const startTime = testRun?.startTime;
  const currentStep = testRun?.currentStep;
  const completedSteps = testRun?.completedSteps || 0;
  const error = testRun?.error;

  useEffect(() => {
    if (!testRun) return;

    if (status === 'running') {
      intervalRef.current = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setElapsedTime(testRun.duration || Date.now() - startTime);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [testRun, status, startTime]);

  if (!testRun) return null;

  const progressPercent = stepsCount > 0 ? Math.round((completedSteps / stepsCount) * 100) : 0;

  const statusConfig = {
    running: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />,
      text: 'text-blue-700 dark:text-blue-300',
      label: 'Running'
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      icon: <CheckCircleIcon className="w-5 h-5 text-green-500" />,
      text: 'text-green-700 dark:text-green-300',
      label: 'Completed'
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      icon: <ExclamationCircleIcon className="w-5 h-5 text-red-500" />,
      text: 'text-red-700 dark:text-red-300',
      label: 'Failed'
    },
    stopped: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      icon: <StopIcon className="w-5 h-5 text-yellow-500" />,
      text: 'text-yellow-700 dark:text-yellow-300',
      label: 'Stopped'
    }
  };

  const config = statusConfig[status] || statusConfig.running;

  return (
    <div className={`mb-6 rounded-lg border ${config.border} ${config.bg} p-4 overflow-hidden`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex-shrink-0">
            {config.icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className={`text-sm font-semibold ${config.text}`}>
                Test Run {config.label}
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Started {moment(startTime).format('HH:mm:ss')}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
              <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">{formatDuration(elapsedTime)}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">{completedSteps}/{stepsCount} steps</span>
              {currentStep && status === 'running' && (
                <>
                  <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                  <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[150px]">{currentStep}</span>
                </>
              )}
            </div>

            {error && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                <span className="font-medium">Error:</span> {error}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Progress bar */}
          <div className="w-32 hidden sm:block">
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${status === 'running' ? 'bg-blue-500' : status === 'success' ? 'bg-green-500' : status === 'error' ? 'bg-red-500' : 'bg-yellow-500'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right">{progressPercent}%</span>

          {status === 'running' && (
            <button
              onClick={onStop}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/50 hover:bg-red-200 dark:hover:bg-red-900 rounded-md transition-colors"
            >
              <StopIcon className="w-4 h-4" />
              Stop
            </button>
          )}
          {status !== 'running' && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// View mode tabs
const ViewTabs = ({ activeView, onViewChange, versionsCount = 0, logsCount = 0, secretsCount = 0, secretsWarning = false }) => {
  const baseClasses = 'px-4 py-2 text-sm font-medium rounded-md transition-colors';
  const activeClasses = 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm';
  const inactiveClasses = 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white';

  const tabs = [
    { id: 'tree', label: 'Tree View' },
    { id: 'steps', label: 'Steps View' },
    { id: 'versions', label: 'Versioning', count: versionsCount },
    { id: 'logs', label: 'Run Logs', count: logsCount },
    { id: 'secrets', label: 'Secrets', count: secretsCount, warning: secretsWarning },
  ];

  return (
    <div className="inline-flex items-stretch p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
      {tabs.map((tab, index) => (
        <div key={tab.id} className="flex items-center">
          {index > 0 && (
            <div className="w-px h-5 bg-gray-300 dark:bg-gray-500 mx-0.5" />
          )}
          <button
            onClick={() => onViewChange(tab.id)}
            className={`${baseClasses} ${activeView === tab.id ? activeClasses : inactiveClasses}`}
          >
            {tab.label}
            {tab.warning && (
              <span className="ml-1 text-red-500" title="Some secrets have empty values">&#9888;</span>
            )}
            {tab.count !== undefined && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${activeView === tab.id
                ? 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300'
                : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                }`}>
                {tab.count}
              </span>
            )}
          </button>
        </div>
      ))}
    </div>
  );
};

const ExecutionNodeDetail = ({ node }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-lg border ${node.status === 'error' ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${node.status === 'error' ? 'bg-red-500' : 'bg-green-500'}`} />
          <span className="text-sm font-medium text-gray-900 dark:text-white">{node.name}</span>
          {node.status === 'error' && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
              error
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span>{node.duration}ms</span>
          <CodeBracketIcon className={`w-4 h-4 transition-transform ${expanded ? 'text-violet-500' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {node.error && (
            <div className="mx-3 mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-600 dark:text-red-400">
              {node.error.message}
            </div>
          )}

          {node.input && node.input.length > 0 && (
            <div className="px-3 pt-3">
              <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Input</div>
              <pre className="p-2 font-mono text-xs bg-gray-900 text-blue-400 overflow-auto max-h-[200px] rounded m-0">
                {JSON.stringify(node.input.length === 1 ? node.input[0] : node.input, null, 2)}
              </pre>
            </div>
          )}

          {node.output && node.output.length > 0 && (
            <div className="px-3 py-3">
              <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Output</div>
              <pre className="p-2 font-mono text-xs bg-gray-900 text-green-400 overflow-auto max-h-[200px] rounded m-0">
                {JSON.stringify(node.output.length === 1 ? node.output[0] : node.output, null, 2)}
              </pre>
            </div>
          )}

          {(!node.input || node.input.length === 0) && (!node.output || node.output.length === 0) && !node.error && (
            <div className="p-3 text-xs text-gray-500 dark:text-gray-400">No input/output data available</div>
          )}
        </div>
      )}
    </div>
  );
};

const PlaybookDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { organisation } = useSelector((state) => state);
  const [playbook, setPlaybook] = useState(null);
  const [playbookLoading, setPlaybookLoading] = useState(true);
  const [viewMode, setViewMode] = useState('tree');
  const [selectedNode, setSelectedNode] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    frequency: 'on_event',
    changes: ''
  });
  const [testRun, setTestRun] = useState(null);
  const testRunTimerRef = useRef(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [secrets, setSecrets] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [executionDetailModal, setExecutionDetailModal] = useState(null);
  const [executionDetailLoading, setExecutionDetailLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  const [webhookCopied, setWebhookCopied] = useState(false);

  // Generation status
  const searchParams = new URLSearchParams(location.search);
  const initialRequestId = searchParams.get('request_id');
  const [requestId, setRequestId] = useState(initialRequestId);
  const [generationStatus, setGenerationStatus] = useState(initialRequestId ? 'polling' : null);
  const [generationResult, setGenerationResult] = useState(null);
  const [refinementAnswers, setRefinementAnswers] = useState({});
  const [refinementSubmitting, setRefinementSubmitting] = useState(false);
  const pollTimerRef = useRef(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const elapsedTimerRef = useRef(null);

  const fetchPlaybook = useCallback(async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Axios(
        API_ROUTES.playbooks['playbook:get'](id),
        {
          method: 'get',
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
          params: { organisation_id: organisation.organisation },
        }
      );
      const data = result.data.data;
      setPlaybook(data);
      setIsEnabled(data.meta?.enabled ?? data.active ?? false);
      setSecrets(data.secrets || []);

      // If no request_id from URL, check latest_request for outstanding work
      if (!initialRequestId && data.latest_request) {
        const req = data.latest_request;
        if (req.status === 'pending' || req.status === 'processing') {
          setRequestId(req.id);
          setGenerationStatus('polling');
        } else if (req.status === 'refinement_required') {
          setRequestId(req.id);
          setGenerationStatus('refinement_required');
          setGenerationResult(req.result);
        } else if (req.status === 'failed' && !data.nodes) {
          setGenerationStatus('failed');
          setGenerationResult(req.result);
        }
      }
    } catch (error) {
      console.error('Failed to fetch playbook:', error);
    } finally {
      setPlaybookLoading(false);
    }
  }, [id, organisation.organisation]);

  useEffect(() => {
    if (organisation.organisation) {
      fetchPlaybook();
    }
  }, [organisation.organisation, fetchPlaybook]);

  // Poll request status
  const pollRequestStatus = useCallback(async (reqId) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Axios(
        API_ROUTES.playbooks['playbook:request'](reqId),
        {
          method: 'get',
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
          params: { organisation_id: organisation.organisation },
        }
      );
      const data = result.data.data;

      if (data.status === 'complete') {
        setGenerationStatus('complete');
        setGenerationResult(data.result);
        setRequestId(null);
        fetchPlaybook();
      } else if (data.status === 'refinement_required') {
        setGenerationStatus('refinement_required');
        setGenerationResult(data.result);
      } else if (data.status === 'failed') {
        setGenerationStatus('failed');
        setGenerationResult(data.result);
      } else {
        // Still pending/processing — poll again
        pollTimerRef.current = setTimeout(() => pollRequestStatus(reqId), 10000);
      }
    } catch (error) {
      console.error('Failed to poll request status:', error);
      setGenerationStatus('failed');
      setGenerationResult({ message: 'Failed to check generation status' });
    }
  }, [organisation.organisation, fetchPlaybook]);

  useEffect(() => {
    if (requestId && organisation.organisation) {
      setGenerationStatus('polling');
      pollRequestStatus(requestId);
    }
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [requestId, organisation.organisation, pollRequestStatus]);

  // Elapsed timer for generation
  useEffect(() => {
    if (generationStatus === 'polling') {
      setElapsedSeconds(0);
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    } else {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    }
    return () => {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, [generationStatus]);

  // Handle refinement submission
  const handleRefinementSubmit = async () => {
    const questions = generationResult?.questions || [];
    const hasAnswers = questions.some((_, idx) => refinementAnswers[idx]?.trim());
    if (!hasAnswers || refinementSubmitting) return;

    setRefinementSubmitting(true);

    // Build a combined response with each question and answer
    const changes = questions
      .map((q, idx) => `Q: ${q}\nA: ${refinementAnswers[idx] || 'No answer provided'}`)
      .join('\n\n');

    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Axios(
        API_ROUTES.playbooks['playbook:update'](id),
        {
          method: 'put',
          data: {
            changes,
            organisation_id: organisation.organisation,
          },
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );
      const newRequestId = result.data.data.request_id;
      setRefinementAnswers({});
      setGenerationResult(null);
      setRequestId(newRequestId);
      setGenerationStatus('polling');
    } catch (error) {
      console.error('Failed to submit refinement:', error);
    } finally {
      setRefinementSubmitting(false);
    }
  };

  // Secrets handlers
  const handleAddSecret = (newSecret) => {
    setSecrets(prev => [...prev, newSecret]);
  };

  const handleEditSecret = (updatedSecret) => {
    setSecrets(prev => prev.map(s => s.id === updatedSecret.id ? { ...s, ...updatedSecret } : s));
  };

  const handleDeleteSecret = async (secretId) => {
    if (window.confirm('Are you sure you want to delete this secret? This action cannot be undone.')) {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const deletedSecret = secrets.find(s => s.id === secretId);
        await Axios(
          API_ROUTES.playbooks['playbook:secret:delete'](id, secretId),
          {
            method: 'delete',
            params: { organisation_id: organisation.organisation },
            headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
          }
        );
        setSecrets(prev => prev.filter(s => s.id !== secretId));
        // Also remove from required_secrets in playbook meta so the banner updates
        if (deletedSecret && playbook?.meta?.required_secrets) {
          setPlaybook(prev => ({
            ...prev,
            meta: {
              ...prev.meta,
              required_secrets: (prev.meta.required_secrets || []).filter(s => s.key !== deletedSecret.key),
            },
          }));
        }
      } catch (error) {
        console.error('Failed to delete secret:', error);
      }
    }
  };

  // Execution detail handler
  const openExecutionDetail = async (executionId) => {
    setExecutionDetailLoading(true);
    setExecutionDetailModal({ loading: true });
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Axios(
        API_ROUTES.playbooks['playbook:execution'](executionId),
        {
          method: 'get',
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );
      setExecutionDetailModal(result.data.data);
    } catch (error) {
      console.error('Failed to fetch execution detail:', error);
      setExecutionDetailModal(null);
    } finally {
      setExecutionDetailLoading(false);
    }
  };

  // Retry execution handler
  const handleRetryExecution = async (executionId) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Axios(
        API_ROUTES.playbooks['playbook:execution:retry'](executionId),
        {
          method: 'post',
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );
      setExecutionDetailModal(null);
      fetchPlaybook();
    } catch (error) {
      console.error('Failed to retry execution:', error);
    }
  };

  // Duplicate handler
  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Axios(
        API_ROUTES.playbooks['playbook:duplicate'](id),
        {
          method: 'post',
          data: { organisation_id: organisation.organisation },
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );
      const newId = result.data.data.id;
      history.push(`/playbooks/playbook/${newId}`);
    } catch (error) {
      console.error('Failed to duplicate playbook:', error);
    } finally {
      setDuplicating(false);
    }
  };

  // Auto-select first node when playbook loads
  useEffect(() => {
    if (playbook?.nodes?.length > 0 && !selectedNode) {
      // Find entry node (no incoming connections) or use first node
      const incomingConnections = {};
      Object.values(playbook.connections || {}).forEach(connData => {
        connData?.main?.forEach(outputs => {
          outputs?.forEach(conn => {
            incomingConnections[conn.node] = true;
          });
        });
      });
      const entryNode = playbook.nodes.find(n => !incomingConnections[n.name] && !n.disabled) || playbook.nodes[0];
      if (entryNode) {
        setSelectedNode({ ...entryNode, integration: getIntegration(entryNode.type) });
      }
    }
  }, [playbook]);

  // Test run handlers
  const [testRunExecutionId, setTestRunExecutionId] = useState(null);

  const startTestRun = useCallback(async () => {
    if (!playbook?.n8n_workflow_id) return;

    const steps = playbook?.nodes || [];

    setTestRun({
      status: 'running',
      startTime: Date.now(),
      currentStep: 'Triggering execution...',
      completedSteps: 0,
      error: null
    });

    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Axios(
        API_ROUTES.playbooks['playbook:execute'](id),
        {
          method: 'post',
          data: { organisation_id: organisation.organisation },
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );

      const executionId = result.data.data.execution_id;
      setTestRunExecutionId(executionId);

      // Poll execution status
      const pollExecution = async () => {
        try {
          const execResult = await Axios(
            API_ROUTES.playbooks['playbook:execution'](executionId),
            {
              method: 'get',
              headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
            }
          );

          const exec = execResult.data.data;

          if (exec.status === 'running') {
            setTestRun(prev => prev ? {
              ...prev,
              completedSteps: exec.stepsExecuted || 0,
              currentStep: exec.nodes?.[exec.nodes.length - 1]?.name || 'Processing...',
            } : null);
            testRunTimerRef.current = setTimeout(pollExecution, 2000);
          } else {
            setTestRun(prev => prev ? {
              ...prev,
              status: exec.status === 'success' ? 'success' : 'error',
              completedSteps: exec.stepsExecuted || steps.length,
              currentStep: null,
              duration: exec.duration || (Date.now() - prev.startTime),
              error: exec.error ? `${exec.error.node}: ${exec.error.message}` : null,
            } : null);
            setTestRunExecutionId(null);
            fetchPlaybook();
          }
        } catch (pollError) {
          console.error('Failed to poll execution:', pollError);
          setTestRun(prev => prev ? {
            ...prev,
            status: 'error',
            currentStep: null,
            duration: Date.now() - prev.startTime,
            error: 'Failed to check execution status',
          } : null);
          setTestRunExecutionId(null);
        }
      };

      testRunTimerRef.current = setTimeout(pollExecution, 2000);
    } catch (error) {
      console.error('Failed to start test run:', error);
      setTestRun(prev => prev ? {
        ...prev,
        status: 'error',
        currentStep: null,
        duration: Date.now() - prev.startTime,
        error: error.response?.data?.message || 'Failed to trigger execution',
      } : null);
    }
  }, [playbook, id, organisation.organisation, fetchPlaybook]);

  const stopTestRun = useCallback(async () => {
    if (testRunTimerRef.current) {
      clearTimeout(testRunTimerRef.current);
    }

    if (testRunExecutionId) {
      try {
        const user = await Auth.currentAuthenticatedUser();
        await Axios(
          API_ROUTES.playbooks['playbook:execution'](testRunExecutionId),
          {
            method: 'post',
            url: API_ROUTES.playbooks['playbook:execution'](testRunExecutionId) + '/../stop',
            headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
          }
        );
      } catch (err) {
        console.error('Failed to stop execution:', err);
      }
      setTestRunExecutionId(null);
    }

    setTestRun(prev => prev ? {
      ...prev,
      status: 'stopped',
      duration: Date.now() - prev.startTime
    } : null);
  }, [testRunExecutionId]);

  const closeTestRun = useCallback(() => {
    setTestRun(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (testRunTimerRef.current) {
        clearTimeout(testRunTimerRef.current);
      }
    };
  }, []);

  // Loading state
  if (playbookLoading) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-32">
        <div className="mb-8">
          <div className="w-24 h-24 rounded-full bg-violet-700 flex items-center justify-center mx-auto shadow-lg shadow-violet-200 dark:shadow-violet-900/50">
            <svg className="w-12 h-12 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Loading playbook</h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md">Fetching your playbook configuration and workflow steps.</p>
      </div>
    );
  }

  const handleNodeSelect = (node) => {
    setSelectedNode(selectedNode?.id === node.id ? null : node);
  };

  const toggleEditModal = () => {
    if (!editModalOpen && playbook) {
      // Initialize form with current playbook data
      const schedule = getPlaybookSchedule(playbook);
      setEditFormData({
        name: playbook.name,
        frequency: schedule === 'on_event' ? 'on_event' : schedule,
        changes: ''
      });
    }
    setEditModalOpen(!editModalOpen);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Axios(
        API_ROUTES.playbooks['playbook:update'](id),
        {
          method: 'put',
          data: {
            name: editFormData.name,
            frequency: 'on_event',
            changes: editFormData.changes || undefined,
            organisation_id: organisation.organisation,
          },
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );
      toggleEditModal();

      const newRequestId = result.data?.data?.request_id;
      if (newRequestId) {
        setRequestId(newRequestId);
        setGenerationStatus('polling');
      } else {
        fetchPlaybook();
      }
    } catch (error) {
      console.error('Failed to update playbook:', error);
    }
  };

  if (!playbook) {
    return (
      <div className="text-center py-12">
        <CommandLineIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Playbook not found</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          The playbook you&apos;re looking for doesn&apos;t exist.
        </p>
        <div className="mt-6">
          <Link
            to="/playbooks"
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-800 hover:bg-violet-600 text-white rounded-md text-sm font-medium transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Playbooks
          </Link>
        </div>
      </div>
    );
  }

  // Derive data from n8n format
  const description = getPlaybookDescription(playbook);
  const schedule = getPlaybookSchedule(playbook);
  const stepsDetail = getStepsDetail(playbook);
  const stepsCount = playbook.nodes?.length || 0;
  // Filter out Felloh system secrets — these are auto-provisioned
  const isFellohSystemSecret = (key) => {
    const k = (key || '').toUpperCase();
    if (!k.includes('FELLOH')) return false;
    return k.includes('API_KEY') || k.includes('PUBLIC_KEY') || k.includes('PRIVATE_KEY')
      || k.includes('SECRET_KEY') || k.includes('ORGANISATION_ID') || k.includes('ORGANIZATION_ID')
      || k.includes('ORG_ID') || k.includes('TOKEN');
  };
  const userSecrets = secrets.filter(s => !isFellohSystemSecret(s.key));
  const userRequiredSecrets = (playbook.meta?.required_secrets || []).filter(s => !isFellohSystemSecret(s.key));

  const hasEmptySecrets = (() => {
    // Only flag secrets that actually exist and are unfilled
    if (userSecrets.some(s => !s.value_last_four)) return true;
    // Only check required keys that still have a matching secret record
    const existingKeys = new Set(userSecrets.map(s => s.key));
    const requiredKeys = userRequiredSecrets.map(s => s.key).filter(k => existingKeys.has(k));
    const filledKeys = new Set(userSecrets.filter(s => s.value_last_four).map(s => s.key));
    return requiredKeys.some(k => !filledKeys.has(k));
  })();
  const runs = playbook.meta?.runs || 0;
  const lastRun = playbook.meta?.lastRun;

  const toggleEnabled = async () => {
    const newState = !isEnabled;
    setIsEnabled(newState);
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Axios(
        API_ROUTES.playbooks['playbook:toggle'](id),
        {
          method: 'patch',
          data: {
            enabled: newState,
            organisation_id: organisation.organisation,
          },
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );
    } catch (error) {
      console.error('Failed to toggle playbook status:', error);
      setIsEnabled(!newState);
    }
  };

  return (
    <React.Fragment>
      <Head title={playbook ? playbook.name : 'Playbook'} />
      <div className="pb-12">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            to="/playbooks"
            className="inline-flex items-center gap-2 text-sm text-violet-800 hover:text-violet-600 dark:text-violet-400 dark:hover:text-violet-300 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Playbooks
          </Link>
        </div>

        {/* Test Run Status Box */}
        <TestRunStatusBox
          testRun={testRun}
          onClose={closeTestRun}
          onStop={stopTestRun}
          stepsCount={playbook.nodes?.length || 0}
        />

        {/* Generation Status */}
        {generationStatus === 'polling' && (
          <div className="flex flex-col items-center justify-center text-center py-12">
            <div className="mb-8 w-full max-w-sm">
              <div className="bg-gray-900 rounded-xl px-4 py-3 shadow-2xl text-left font-mono overflow-hidden">
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  <span className="ml-2 text-[10px] text-gray-500">playbook.json</span>
                </div>
                <div className="space-y-1 text-[11px] leading-relaxed">
                  <div className="text-gray-500">{'{'}</div>
                  <div className="pl-3"><span className="text-violet-400">"nodes"</span><span className="text-gray-500">: [{'{'}</span></div>
                  <div className="pl-6 animate-pulse"><span className="text-emerald-400">"name"</span><span className="text-gray-500">:</span> <span className="text-amber-300">"Felloh Webhook"</span></div>
                  <div className="pl-3"><span className="text-gray-500">{'}, {'}</span></div>
                  <div className="pl-6 animate-pulse" style={{ animationDelay: '300ms' }}><span className="text-emerald-400">"name"</span><span className="text-gray-500">:</span> <span className="text-amber-300">"Transform"</span></div>
                  <div className="pl-3"><span className="text-gray-500">{'}]'}</span></div>
                  <div className="text-gray-500">{'}'}</div>
                </div>
                <div className="mt-2 flex items-center gap-1">
                  <span className="w-1.5 h-3 bg-violet-500 animate-pulse" />
                  <span className="text-[10px] text-gray-600">writing...</span>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {playbook?.nodes ? 'Re-generating your playbook' : 'Generating your playbook'}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mb-6">
              {playbook?.nodes
                ? 'Our AI is applying your changes and rebuilding the workflow.'
                : 'Our AI is analysing your description, selecting the right integrations, and building the workflow steps.'}
            </p>
            <div className="flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400">
              <SparklesIcon className="w-4 h-4" />
              This usually takes 30 seconds to 2 minutes depending on the complexity of the workflow.
            </div>
            <div className="mt-8 flex items-center gap-2 text-lg text-gray-400 dark:text-gray-500 font-mono">
              <ClockIcon className="w-4 h-4" />
              {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')} elapsed
            </div>
          </div>
        )}

        {generationStatus === 'refinement_required' && generationResult && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" style={{ display: 'block' }}>
            {/* Header */}
            <div className="bg-violet-700 px-8 py-6 clearfix h-auto">
              <div>
                <div className="h-full float-left mr-3">
                  <SparklesIcon className="w-8 h-8 text-white mt-1.5" />
                </div>
                <div className='float-left'>
                  <h2 className="text-lg font-bold text-white">
                    A few questions before we build
                  </h2>
                  <p className="text-sm text-white">
                    Help us tailor the workflow to your exact needs
                  </p>
                </div>
              </div>
            </div>

            {/* Questions */}
            <div className="px-8 py-6 space-y-6">
              {(generationResult.questions || []).map((question, idx) => (
                <div key={idx}>
                  <div className="flex items-start gap-3 mb-2 clearfix h-auto">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 text-xs font-semibold flex items-center justify-center mt-0.5">
                      {idx + 1}
                    </span>
                    <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 leading-relaxed">
                      {question}
                    </label>
                  </div>
                  <div className="ml-9">
                    <textarea
                      value={refinementAnswers[idx] || ''}
                      onChange={(e) => setRefinementAnswers(prev => ({ ...prev, [idx]: e.target.value }))}
                      placeholder="Your answer..."
                      rows={2}
                      className="block w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 focus:bg-white dark:focus:bg-gray-800 text-sm resize-none transition-colors"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-8 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={handleRefinementSubmit}
                disabled={refinementSubmitting || !(generationResult.questions || []).some((_, idx) => refinementAnswers[idx]?.trim())}
                className="h-auto clearfix inline-flex items-center gap-2 px-5 py-2.5 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-medium transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed shadow-sm"
              >
                {refinementSubmitting ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-4 h-4" />
                    Continue
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {generationStatus === 'failed' && (
          <div className="flex flex-col items-center justify-center text-center py-32">
            <div className="mb-8">
              <div className="w-24 h-24 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
                <ExclamationCircleIcon className="w-12 h-12 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <h2 className="text-2xl font-normal text-gray-900 dark:text-white mb-3 font-mono uppercase">
              Playbook generation failed
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mb-8">
              {generationResult?.message || 'Something went wrong while generating your playbook. This can happen due to high demand or complex workflows. Please try again.'}
            </p>
            <button
              onClick={async () => {
                try {
                  const user = await Auth.currentAuthenticatedUser();
                  const res = await Axios(
                    API_ROUTES.playbooks['playbook:update'](id),
                    {
                      method: 'put',
                      data: {
                        changes: 'Retry: regenerate the playbook with the same requirements',
                        organisation_id: organisation.organisation,
                      },
                      headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
                    }
                  );
                  const newRequestId = res.data.data.request_id;
                  setGenerationResult(null);
                  setRequestId(newRequestId);
                  setGenerationStatus('polling');
                } catch (err) {
                  console.error('Failed to retry:', err);
                }
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Retry Generation
            </button>
          </div>
        )}

        {generationStatus === 'complete' && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg px-4 py-4 clearfix" style={{ display: 'block', overflow: 'auto' }}>
            <button
              onClick={() => setGenerationStatus(null)}
              className="float-right text-green-600 hover:text-green-800 dark:text-green-400 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 mb-1">
              <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <h3 className="font-bold font-mono uppercase text-green-800 dark:text-green-300 text-sm">
                Playbook generated successfully
              </h3>
            </div>
            {generationResult?.summary && (
              <p className="text-sm text-green-700 dark:text-green-400 ml-7">{generationResult.summary}</p>
            )}
          </div>
        )}

        {/* Required Secrets Banner */}
        {(() => {
          if (generationStatus === 'polling') return null;
          if (!userRequiredSecrets.length) return null;
          // Only consider required secrets that still have a matching secret record
          const existingKeys = new Set(userSecrets.map(s => s.key));
          const filledKeys = new Set(userSecrets.filter(s => s.value_last_four).map(s => s.key));
          const missingSecrets = userRequiredSecrets.filter(s => existingKeys.has(s.key) && !filledKeys.has(s.key));
          if (missingSecrets.length === 0) return null;
          return (
            <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg px-4 py-4 clearfix" style={{ display: 'block', overflow: 'auto' }}>
              <button
                onClick={() => {
                  setViewMode('secrets');
                  setTimeout(() => {
                    document.getElementById('secrets-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                className="float-right clearfix h-auto mt-1 items-center gap-1 px-3 py-1.5 text-sm font-medium text-amber-800 dark:text-amber-300 border border-amber-400 bg-amber-200 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 rounded-md transition-colors"
              >
                <KeyIcon className="w-4 h-4 inline-block mr-1" />
                Configure Secrets
              </button>
              <h3 className="font-mono uppercase font-semibold text-amber-800 dark:text-amber-300 mb-1">
                <KeyIcon className="w-4 h-4 inline -mt-0.5 mr-1" />
                Credentials Required
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                This playbook needs {missingSecrets.length} secret{missingSecrets.length > 1 ? 's' : ''} to be configured before it can run.
              </p>
            </div>
          );
        })()}

        {/* Header */}
        {generationStatus !== 'refinement_required' && generationStatus !== 'polling' && generationStatus !== 'failed' && (<>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6" style={{ overflow: 'hidden' }}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {playbook.name}
                  </h1>
                  {isEnabled ? (
                    <span className="inline-flex clearfix items-center gap-1 px-3 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                      <PlayIcon className="w-4 h-4" />
                      Enabled
                    </span>
                  ) : (
                    <span className="inline-flex clearfix items-center gap-1 px-3 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300">
                      <PauseIcon className="w-4 h-4" />
                      Disabled
                    </span>
                  )}
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {playbook.version_id}
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {description}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={handleDuplicate}
                  disabled={duplicating}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-violet-400 dark:border-violet-600 text-violet-700 dark:text-violet-400 rounded-md text-sm font-medium hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                  {duplicating ? 'Duplicating...' : 'Duplicate'}
                </button>
                <div className="relative group">
                  <button
                    onClick={toggleEnabled}
                    disabled={hasEmptySecrets && !isEnabled}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${hasEmptySecrets && !isEnabled
                      ? 'bg-gray-100 border border-gray-300 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-gray-500'
                      : isEnabled
                        ? 'bg-orange-500 border border-orange-500 text-white hover:bg-amber-700 dark:bg-amber-700 dark:border-amber-800 dark:text-white dark:hover:bg-amber-800'
                        : 'bg-green-700 border border-green-700 text-white hover:bg-green-700 dark:bg-green-700 dark:border-green-800 dark:text-white dark:hover:bg-green-800'
                      }`}
                    style={hasEmptySecrets && !isEnabled ? { pointerEvents: 'none' } : undefined}
                  >
                    {isEnabled ? (
                      <><PauseIcon className="w-4 h-4" /> Disable</>
                    ) : (
                      <><PlayIcon className="w-4 h-4" /> Enable</>
                    )}
                  </button>
                  {hasEmptySecrets && !isEnabled && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      Configure all secrets before enabling
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900" />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setDeleteModalOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 border border-red-700 text-white rounded-md text-sm font-medium hover:bg-red-700 dark:bg-red-700 dark:border-red-800 dark:hover:bg-red-800 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete
                </button>
                <div className="relative group">
                  <button
                    onClick={startTestRun}
                    disabled={testRun?.status === 'running' || hasEmptySecrets}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-violet-400 dark:border-violet-600 text-violet-700 dark:text-violet-400 rounded-md text-sm font-medium transition-colors ${testRun?.status === 'running' || hasEmptySecrets
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-violet-50 dark:hover:bg-violet-900/20'
                      }`}
                    style={hasEmptySecrets ? { pointerEvents: 'none' } : undefined}
                  >
                    <PlayIcon className="w-4 h-4" />
                    Test Run
                  </button>
                  {hasEmptySecrets && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      Configure all secrets before running
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900" />
                    </div>
                  )}
                </div>
                <button
                  onClick={toggleEditModal}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-violet-400 dark:border-violet-600 text-violet-700 dark:text-violet-400 rounded-md text-sm font-medium hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                >
                  <SparklesIcon className="w-4 h-4" />
                  AI Edit
                </button>
                <Link
                  to={`/playbooks/playbook/${id}/edit`}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-violet-400 dark:border-violet-600 text-violet-700 dark:text-violet-400 rounded-md text-sm font-medium hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                >
                  <CodeBracketIcon className="w-4 h-4" />
                  Manual Edit
                </Link>
              </div>
            </div>

            <div className="clearfix h-auto relative pt-4 border-t border-gray-200">
              {(() => {
                const logs = playbook.logs || [];
                const successCount = logs.filter(l => l.status === 'success').length;
                const errorCount = logs.filter(l => l.status === 'error').length;
                const successRate = logs.length > 0 ? Math.round((successCount / logs.length) * 100) : null;
                const avgDuration = logs.length > 0 ? Math.round(logs.reduce((sum, l) => sum + (l.duration || 0), 0) / logs.length) : null;

                return (
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4 dark:border-gray-700 clearfix h-auto">
                    <div className="flex items-center gap-3 clearfix h-auto">
                      <CommandLineIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono uppercase">Steps</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">{stepsCount}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 clearfix h-auto">
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono uppercase">Schedule</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">{getScheduleLabel(schedule)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 clearfix h-auto">
                      <ChartBarIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono uppercase">Total Runs</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {runs}
                          {logs.length > 0 && (
                            <span className="ml-2 text-xs font-normal">
                              <span className="text-green-600">{successCount}</span>
                              {errorCount > 0 && <span className="text-red-500 ml-1">/ {errorCount} err</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 clearfix h-auto">
                      <CheckCircleIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono uppercase">Success Rate</div>
                        <div className={`text-lg font-semibold ${successRate !== null && successRate < 80 ? 'text-red-600' : successRate !== null && successRate < 95 ? 'text-amber-600' : 'text-gray-900 dark:text-white'}`}>
                          {successRate !== null ? `${successRate}%` : '-'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 clearfix h-auto">
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono uppercase">Avg Duration</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {avgDuration !== null ? (avgDuration > 1000 ? `${(avgDuration / 1000).toFixed(1)}s` : `${avgDuration}ms`) : '-'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 clearfix h-auto">
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono uppercase">Last Run</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {lastRun ? moment(lastRun).format('MMM D [at] HH:mm') : 'Never'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Webhook URL */}
            {playbook.webhook_url && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <LinkIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-mono uppercase flex-shrink-0">Webhook</div>
                  <div className="flex-1 flex items-center gap-2 min-w-0">
                    <code className="text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-900 px-3 py-1.5 rounded-md truncate block flex-1 font-mono">
                      {playbook.webhook_url.replace('playbooks.vpn.felloh.com', 'playbooks.felloh.com')}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(playbook.webhook_url.replace('playbooks.vpn.felloh.com', 'playbooks.felloh.com'));
                        setWebhookCopied(true);
                        setTimeout(() => setWebhookCopied(false), 2000);
                      }}
                      className="flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                      title="Copy webhook URL"
                    >
                      {webhookCopied ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      ) : (
                        <ClipboardDocumentIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 clearfix h-auto mb-4">
            <ViewTabs
              activeView={viewMode}
              onViewChange={setViewMode}
              versionsCount={Math.max((playbook.versions || []).length, 1)}
              logsCount={(playbook.logs || []).length}
              secretsCount={userSecrets.length}
              secretsWarning={hasEmptySecrets}
            />
          </div>

          {/* Workflow Section */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            {viewMode === 'tree' && (
              <div className="flex items-center justify-between mb-6 flex-wrap">
                {/* Legend - only show for tree view */}

                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-green-500 bg-white" />
                    <span className="text-gray-600 dark:text-gray-400">Success</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-red-400 bg-white" />
                    <span className="text-gray-600 dark:text-gray-400">Error</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-violet-400 bg-white" />
                    <span className="text-gray-600 dark:text-gray-400">Path</span>
                  </div>
                </div>

              </div>
            )}

            <div className="flex gap-6">
              <div className="flex-1 min-w-0 overflow-x-auto pb-4">
                {viewMode === 'tree' && (
                  playbook?.nodes?.length > 0 ? (
                    <WorkflowTree
                      n8nNodes={playbook.nodes}
                      connections={playbook.connections}
                      onNodeSelect={handleNodeSelect}
                      selectedNodeId={selectedNode?.id}
                    />
                  ) : (
                    <LegacyStepsList steps={stepsDetail} />
                  )
                )}

                {viewMode === 'steps' && (
                  <StepsView n8nNodes={playbook?.nodes} connections={playbook?.connections} />
                )}

                {viewMode === 'versions' && (
                  <div className="space-y-3">
                    {(playbook.versions && playbook.versions.length > 0) ? (
                      [...playbook.versions]
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((version) => (
                          <div
                            key={version.id}
                            className={`p-4 rounded-lg border ${version.is_current
                              ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                              : 'border-gray-200 dark:border-gray-700'
                              }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-base font-semibold text-gray-900 dark:text-white">
                                  {version.version}
                                </span>
                                {version.is_current && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300">
                                    Current
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  {moment(version.created_at).format('MMM D, YYYY [at] HH:mm')}
                                </span>
                                {!version.is_current && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const user = await Auth.currentAuthenticatedUser();
                                        await Axios(
                                          API_ROUTES.playbooks['playbook:revert'](id, version.id),
                                          {
                                            method: 'post',
                                            data: { organisation_id: organisation.organisation },
                                            headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
                                          }
                                        );
                                        fetchPlaybook();
                                      } catch (err) {
                                        console.error('Failed to revert:', err);
                                      }
                                    }}
                                    className="inline-flex h-auto items-center gap-1 px-3 py-1 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/50 hover:bg-violet-200 dark:hover:bg-violet-900 rounded-md transition-colors"
                                  >
                                    <ArrowPathIcon className="w-4 h-4" />
                                    Revert
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {version.change_description}
                            </p>
                            {(version.first_name || version.email) && (
                              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                by {version.first_name ? `${version.first_name} ${version.last_name || ''}`.trim() : version.email}
                              </p>
                            )}
                          </div>
                        ))
                    ) : (
                      <div className="p-4 rounded-lg border border-violet-500 bg-violet-50 dark:bg-violet-900/20">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-base font-semibold text-gray-900 dark:text-white">
                              v1
                            </span>
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300">
                              Current
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Initial version
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {viewMode === 'logs' && (
                  <div className="space-y-2 pt-1">
                    {/* Log Filters */}
                    {playbook.logs && playbook.logs.length > 0 && (
                      <div className="flex items-center gap-2 mb-4 pt-1">
                        <FunnelIcon className="w-4 h-4 text-gray-400" />
                        {['all', 'success', 'error', 'warning'].map((filter) => {
                          const count = filter === 'all'
                            ? playbook.logs.length
                            : playbook.logs.filter(l => l.status === filter).length;
                          if (filter !== 'all' && count === 0) return null;
                          return (
                            <button
                              key={filter}
                              onClick={() => setLogFilter(filter)}
                              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${logFilter === filter
                                ? filter === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                  : filter === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                    : filter === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                      : 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                              {filter.charAt(0).toUpperCase() + filter.slice(1)} ({count})
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {(playbook.logs && playbook.logs.length > 0) ? (
                      [...playbook.logs]
                        .filter(log => logFilter === 'all' || log.status === logFilter)
                        .sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))
                        .map((log) => (
                          <div
                            key={log.id}
                            onClick={() => openExecutionDetail(log.executionId)}
                            className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                {log.status === 'success' && (
                                  <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                                )}
                                {log.status === 'error' && (
                                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                )}
                                {log.status === 'warning' && (
                                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                                )}
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {log.triggerData?.booking_reference || log.executionId}
                                </span>
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${log.status === 'success'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                  : log.status === 'error'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                  }`}>
                                  {log.status}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  {moment(log.startedAt).format('MMM D, YYYY [at] HH:mm:ss')}
                                </span>
                                {log.status === 'error' && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleRetryExecution(log.executionId); }}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/50 hover:bg-violet-200 dark:hover:bg-violet-900 rounded-md transition-colors"
                                  >
                                    <ArrowPathIcon className="w-3 h-3" />
                                    Retry
                                  </button>
                                )}
                                <EyeIcon className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                            <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-gray-400">
                              {log.stepsExecuted > 0 && (
                                <span>{log.stepsExecuted} steps executed</span>
                              )}
                              <span>{log.duration}ms duration</span>
                              {log.version && (
                                <span>v{log.version}</span>
                              )}
                              {log.triggerData?.amount && (
                                <span>£{log.triggerData.amount.toLocaleString()}</span>
                              )}
                            </div>
                            {log.error && (
                              <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-600 dark:text-red-400">
                                <span className="font-medium">{log.error.step}:</span> {log.error.message}
                              </div>
                            )}
                            {log.warning && (
                              <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-sm text-yellow-600 dark:text-yellow-400">
                                <span className="font-medium">{log.warning.step}:</span> {log.warning.message}
                              </div>
                            )}
                          </div>
                        ))
                    ) : (
                      <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                        <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No run logs yet</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Run logs will appear here once the playbook has been executed.
                        </p>
                        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                          Use the &quot;Test Run&quot; button above to trigger a test execution.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {viewMode === 'secrets' && (
                  <div id="secrets-section">
                    <SecretsView
                      secrets={userSecrets}
                      onAddSecret={handleAddSecret}
                      onEditSecret={handleEditSecret}
                      onDeleteSecret={handleDeleteSecret}
                      isAdmin={organisation.is_admin}
                    />
                  </div>
                )}
              </div>

              {/* Detail panel */}
              {viewMode === 'tree' && selectedNode && (
                <div className="flex-shrink-0">
                  <StepDetailPanel
                    node={selectedNode}
                    onClose={() => setSelectedNode(null)}
                    onNavigateToNode={handleNodeSelect}
                  />
                </div>
              )}
            </div>
          </div>
        </>)}

        {/* Delete Playbook Modal */}
        <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} className="modal-dialog-centered" size="md">
          <ModalHeader
            toggle={() => setDeleteModalOpen(false)}
            className="bg-red-600 text-xl text-white pt-4 pb-3"
            close={
              <button className="-mt-1 text-white" onClick={() => setDeleteModalOpen(false)}>
                <XMarkIcon className="h-6 w-6" />
              </button>
            }
          >
            <span className="flex items-center gap-2">
              <TrashIcon className="h-5 w-5" />
              Delete Playbook
            </span>
          </ModalHeader>
          <ModalBody className="p-6">
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              Are you sure you want to delete <strong>{playbook.name}</strong>?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This will permanently remove the playbook, all versions, secrets, the n8n workflow, and the Felloh webhook. This action cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter className="bg-light">
            <button
              type="button"
              onClick={() => setDeleteModalOpen(false)}
              className="btn btn-outline-secondary"
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  const user = await Auth.currentAuthenticatedUser();
                  await Axios(
                    API_ROUTES.playbooks['playbook:delete'](id),
                    {
                      method: 'delete',
                      params: { organisation_id: organisation.organisation },
                      headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
                    }
                  );
                  history.push('/playbooks/summary');
                } catch (err) {
                  console.error('Failed to delete playbook:', err);
                  setDeleting(false);
                }
              }}
              className="btn bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {deleting ? 'Deleting...' : 'Delete Playbook'}
            </button>
          </ModalFooter>
        </Modal>

        {/* Edit Playbook Modal */}
        <Modal isOpen={editModalOpen} toggle={toggleEditModal} size="lg">
          <ModalHeader
            toggle={toggleEditModal}
            className="bg-violet-800 text-xl text-white pt-4 pb-3"
            close={
              <button className="-mt-1 text-white" onClick={toggleEditModal}>
                <XMarkIcon className="h-6 w-6" />
              </button>
            }
          >
            <span className="flex items-center gap-2">
              <PencilIcon className="h-5 w-5" />
              Edit Playbook
            </span>
          </ModalHeader>

          <form onSubmit={handleEditSubmit}>
            <ModalBody className="p-6">
              <div className="space-y-6">
                {/* Name Field */}
                <div>
                  <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Playbook Name
                  </label>
                  <input
                    type="text"
                    id="edit-name"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditInputChange}
                    placeholder="e.g., Payment Confirmation Flow"
                    className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    required
                  />
                </div>

                {/* Changes Description Field - LLM Style */}
                <div>
                  <label htmlFor="edit-changes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    What changes would you like to make?
                  </label>
                  <div className="relative">
                    <textarea
                      id="edit-changes"
                      name="changes"
                      value={editFormData.changes}
                      onChange={handleEditInputChange}
                      placeholder="Describe the changes you want to make in natural language. For example: 'Add an additional check to verify the customer email before sending the confirmation. Also add a Slack notification when a payment fails.'"
                      rows={6}
                      className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                    />
                    <div className="absolute bottom-3 right-3 flex items-center gap-1 text-xs text-gray-400">
                      <SparklesIcon className="h-4 w-4" />
                      AI-powered
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Our AI will analyse your changes and update the playbook steps automatically.
                  </p>
                </div>
              </div>
            </ModalBody>

            <ModalFooter className="bg-light">
              <button
                type="button"
                onClick={toggleEditModal}
                className="btn btn-outline-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn bg-violet-700 hover:bg-violet-800 text-white"
              >
                <SparklesIcon className="w-4 h-4 inline mr-1 -mt-0.5" />
                Update Playbook
              </button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Execution Detail Modal */}
        <Modal isOpen={!!executionDetailModal} toggle={() => setExecutionDetailModal(null)} size="xl">
          <ModalHeader
            toggle={() => setExecutionDetailModal(null)}
            className="bg-violet-800 text-xl text-white pt-4 pb-3"
            close={
              <button className="-mt-1 text-white" onClick={() => setExecutionDetailModal(null)}>
                <XMarkIcon className="h-6 w-6" />
              </button>
            }
          >
            <span className="flex items-center gap-2">
              <EyeIcon className="h-5 w-5" />
              Execution Detail
              {executionDetailModal && !executionDetailModal.loading && (
                <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${executionDetailModal.status === 'success'
                    ? 'bg-green-100 text-green-800'
                    : executionDetailModal.status === 'error'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                  {executionDetailModal.status}
                </span>
              )}
            </span>
          </ModalHeader>
          <ModalBody className="p-0">
            {executionDetailLoading ? (
              <div className="flex items-center justify-center py-12">
                <ArrowPathIcon className="w-6 h-6 text-violet-500 animate-spin" />
                <span className="ml-2 text-sm text-gray-500">Loading execution detail...</span>
              </div>
            ) : executionDetailModal && !executionDetailModal.loading ? (
              <div>
                {/* Summary bar */}
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                  <span>Execution <strong className="text-gray-900 dark:text-white">{executionDetailModal.id}</strong></span>
                  {executionDetailModal.startedAt && (
                    <span>{moment(executionDetailModal.startedAt).format('MMM D, YYYY [at] HH:mm:ss')}</span>
                  )}
                  <span>{executionDetailModal.duration}ms</span>
                  <span>{executionDetailModal.stepsExecuted} steps</span>
                </div>

                {/* Node-by-node results */}
                <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
                  {executionDetailModal.error && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                      <span className="font-semibold">{executionDetailModal.error.node}:</span> {executionDetailModal.error.message}
                    </div>
                  )}

                  {(executionDetailModal.nodes || []).map((node, idx) => (
                    <ExecutionNodeDetail key={idx} node={node} />
                  ))}

                  {(!executionDetailModal.nodes || executionDetailModal.nodes.length === 0) && !executionDetailModal.error && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <p className="text-sm">No node execution data available for this run.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </ModalBody>
          <ModalFooter className="bg-light">
            {executionDetailModal && executionDetailModal.status === 'error' && (
              <button
                onClick={() => handleRetryExecution(executionDetailModal.id)}
                className="btn bg-violet-700 hover:bg-violet-800 text-white"
              >
                <ArrowPathIcon className="w-4 h-4 inline mr-1 -mt-0.5" />
                Retry Execution
              </button>
            )}
            <button
              type="button"
              onClick={() => setExecutionDetailModal(null)}
              className="btn btn-outline-secondary"
            >
              Close
            </button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default PlaybookDetail;
